/**
 * Main-thread sparkle/pixel animation engine for <dees-sparklebox>.
 *
 * Ported from the original OffscreenCanvas worker implementation so the element
 * stays self-contained — no separately-served worker file to deploy. The field
 * is a grid of square "pixels" that grow in from the centre outwards and then
 * shimmer. Visibility and reduced-motion gating live in the element; the engine
 * only knows how to draw, resize and (dis)appear.
 */

export interface ISparkleFieldOptions {
  /** grid spacing in px (already validated by the caller) */
  gap: number;
  /** list of fill colors picked at random per pixel */
  colors: string[];
  /** shimmer speed on a 0–100 scale; 0 = static (reduced motion) */
  speed: number;
}

export type TSparkleAction = 'appear' | 'disappear';

class Pixel {
  public size = 0;
  public minSize = 0.5;
  public counter = 0;
  public counterStep: number;
  public speed: number;
  public isIdle = false;
  public isReverse = false;
  public isShimmer = false;

  constructor(
    public x: number,
    public y: number,
    public color: string,
    speed: number,
    public delay: number,
    public maxSize: number,
  ) {
    this.speed = speed * (Math.random() * (0.9 - 0.1) + 0.1);
    this.counterStep = Math.random() * 4;
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    const centerOffset = (this.maxSize - this.size) * 0.5;
    ctx.fillStyle = this.color;
    ctx.fillRect(this.x + centerOffset, this.y + centerOffset, this.size, this.size);
  }

  public appear(ctx: CanvasRenderingContext2D): void {
    if (this.counter < this.delay) {
      this.counter += this.counterStep;
      return;
    }
    if (this.size >= this.maxSize) {
      this.isShimmer = true;
    }
    if (this.isShimmer) {
      this.shimmer();
    } else {
      this.size += 0.2;
    }
    this.draw(ctx);
  }

  public disappear(ctx: CanvasRenderingContext2D): void {
    this.isShimmer = false;
    this.counter = 0;
    if (this.size <= 0) {
      this.isIdle = true;
      return;
    }
    this.size -= 0.1;
    this.draw(ctx);
  }

  /** static render — jump straight to full size (used for reduced motion) */
  public fill(ctx: CanvasRenderingContext2D): void {
    this.size = this.maxSize;
    this.draw(ctx);
  }

  private shimmer(): void {
    if (this.size >= this.maxSize) {
      this.isReverse = true;
    } else if (this.size <= this.minSize) {
      this.isReverse = false;
    }
    this.size += this.isReverse ? -this.speed : this.speed;
  }
}

export class SparkleField {
  private ctx: CanvasRenderingContext2D;
  private gap = 4;
  private colors: string[] = ['#ffffff'];
  private speed = 0.035;
  private pixels: Pixel[] = [];
  private rafId: number | null = null;
  private action: TSparkleAction = 'appear';

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('dees-sparklebox: 2D canvas context unavailable');
    }
    this.ctx = ctx;
  }

  /** Apply drawing options. Recreates pixels if the canvas is already sized. */
  public configure(options: ISparkleFieldOptions): void {
    this.gap = Math.min(50, Math.max(4, options.gap));
    this.colors = options.colors.length ? options.colors : ['#ffffff'];
    this.speed = Math.min(100, Math.max(0, options.speed)) * 0.001;
    if (this.canvas.width > 0 && this.canvas.height > 0) {
      this.createPixels();
    }
  }

  public resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    this.createPixels();
  }

  public play(action: TSparkleAction): void {
    this.action = action;
    if (!this.pixels.length) {
      return;
    }
    if (this.speed === 0) {
      // reduced motion / static: paint a full field once, no animation loop
      this.renderStatic();
      return;
    }
    if (action === 'appear') {
      for (const pixel of this.pixels) {
        pixel.isIdle = false;
      }
    }
    if (this.rafId === null) {
      this.loop();
    }
  }

  public stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  public destroy(): void {
    this.stop();
    this.pixels = [];
  }

  private renderStatic(): void {
    this.stop();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    for (const pixel of this.pixels) {
      pixel.fill(this.ctx);
    }
  }

  private createPixels(): void {
    const { width, height } = this.canvas;
    this.pixels = [];
    for (let x = 0; x < width; x += this.gap) {
      for (let y = 0; y < height; y += this.gap) {
        const color = this.colors[Math.floor(Math.random() * this.colors.length)];
        const dx = x - width / 2;
        const dy = y - height / 2;
        const delay = Math.sqrt(dx * dx + dy * dy) / 2;
        const maxSize = Math.random() * 2 + 1;
        this.pixels.push(new Pixel(x, y, color, this.speed, delay, maxSize));
      }
    }
  }

  private loop = (): void => {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    let allIdle = true;
    for (const pixel of this.pixels) {
      if (this.action === 'appear') {
        pixel.appear(this.ctx);
        allIdle = false;
      } else {
        pixel.disappear(this.ctx);
        if (!pixel.isIdle) {
          allIdle = false;
        }
      }
    }
    if (!allIdle) {
      this.rafId = requestAnimationFrame(this.loop);
    } else {
      this.rafId = null;
    }
  };
}
